import { ArrowPathIcon } from '@heroicons/react/24/outline';

import { Badge } from '../badge';
import { Tooltip } from '../tooltip';

export interface JobUpdatedBadgeProps {
  /**
   * Translated label shown inside the badge (e.g. "Offerta aggiornata").
   * Kept as a prop so each app can pass its own i18n key.
   */
  label: string;
  /**
   * Optional longer explanation shown in the tooltip on hover. When omitted, the
   * badge renders without a tooltip.
   */
  tooltipText?: string;
  /**
   * Timestamp of the last material update, rendered beneath the tooltip when
   * provided. Free-form string so the caller controls date formatting.
   */
  updatedAtLabel?: string;
}

/**
 * Inline badge shown on candidate cards (employer-app kanban + shortlist) and on
 * the worker's own applications list when the underlying JobPosition has been
 * edited after the relevant reference date (`added_to_shortlist_at` for the
 * employer-facing kanban, `inserted_at` for the worker's own view).
 *
 * Pure presentation: visibility rules live in the calling component.
 */
export const JobUpdatedBadge = ({ label, tooltipText, updatedAtLabel }: JobUpdatedBadgeProps) => {
  const badgeEl = (
    <Badge variant="warning">
      <span className="inline-flex items-center gap-1 leading-none">
        <ArrowPathIcon className="h-3.5 w-3.5" />
        <span>{label}</span>
      </span>
    </Badge>
  );

  if (!tooltipText) return badgeEl;

  return (
    <Tooltip
      // High z so it floats above the worker-app navbar (z-50) on the
      // candidature detail page; otherwise the popup is clipped by the header.
      className="max-w-[320px] text-center !z-[9999]"
      content={
        <div className="text-left max-w-[260px] text-xs">
          <div>{tooltipText}</div>
          {updatedAtLabel && <div className="mt-1 text-xs text-white opacity-80">{updatedAtLabel}</div>}
        </div>
      }
    >
      {badgeEl}
    </Tooltip>
  );
};

export default JobUpdatedBadge;
